import React from 'react';
import { Chip } from '@mui/material';
import type { ChipProps, PaletteMode } from '@mui/material';
import { AppointmentStatus, InvoiceStatus, PaymentStatus, TaskStatus } from '../../types';
import { semantic } from '../../theme/tokens';
import type { SemanticTone } from '../../theme/tokens';

/**
 * The one place status → colour + label is decided.
 *
 * Previously this logic was duplicated (with differing colours) across
 * SchedulePage, AppointmentTrackingPage, and AppointmentsPage.
 */

type AnyStatus = AppointmentStatus | TaskStatus | InvoiceStatus | PaymentStatus | string;

interface StatusMeta {
  label: string;
  tone: SemanticTone;
}

const STATUS_MAP: Record<string, StatusMeta> = {
  // Appointments
  [AppointmentStatus.PENDING]: { label: 'Pending', tone: 'warning' },
  [AppointmentStatus.CONFIRMED]: { label: 'Confirmed', tone: 'info' },
  [AppointmentStatus.IN_PROGRESS]: { label: 'In progress', tone: 'info' },
  [AppointmentStatus.COMPLETED]: { label: 'Completed', tone: 'success' },
  [AppointmentStatus.READY_FOR_PICKUP]: { label: 'Ready for pickup', tone: 'success' },
  [AppointmentStatus.CLOSED_OUT]: { label: 'Closed out', tone: 'neutral' },
  [AppointmentStatus.CANCELLED]: { label: 'Canceled', tone: 'danger' },

  // Tasks (pending/in_progress/completed/cancelled share keys above)
  [TaskStatus.FINISHED]: { label: 'Finished', tone: 'success' },

  // Invoices
  [InvoiceStatus.DRAFT]: { label: 'Draft', tone: 'neutral' },
  [InvoiceStatus.SENT]: { label: 'Sent', tone: 'info' },
  [InvoiceStatus.PAID]: { label: 'Paid', tone: 'success' },

  // Payments (pending/completed share the appointment keys above; note
  // PaymentStatus.CANCELED is Square's one-L spelling, a distinct key from
  // AppointmentStatus.CANCELLED)
  [PaymentStatus.FAILED]: { label: 'Failed', tone: 'danger' },
  [PaymentStatus.CANCELED]: { label: 'Voided', tone: 'neutral' },
  [PaymentStatus.REFUNDED]: { label: 'Refunded', tone: 'info' },

  // Purchase orders (draft/confirmed/cancelled/failed share keys above)
  submitted: { label: 'Ordered', tone: 'info' },
  partially_received: { label: 'Partly received', tone: 'warning' },
  received: { label: 'Received', tone: 'success' },

  // Integrations — webhook deliveries (pending/failed share keys above) and
  // provider connections.
  delivered: { label: 'Delivered', tone: 'success' },
  active: { label: 'Active', tone: 'success' },
  error: { label: 'Needs attention', tone: 'danger' },
  disconnected: { label: 'Disconnected', tone: 'neutral' },
};

/** Title-cases an unknown status so it never renders as raw snake_case. */
const humanize = (status: string) =>
  status
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

export const getStatusMeta = (status: AnyStatus): StatusMeta =>
  STATUS_MAP[status] ?? { label: humanize(String(status)), tone: 'neutral' };

interface StatusChipProps extends Omit<ChipProps, 'color' | 'label'> {
  status: AnyStatus;
  /** Overrides the derived label. */
  label?: string;
}

export const StatusChip: React.FC<StatusChipProps> = ({ status, label, sx, ...chipProps }) => {
  const meta = getStatusMeta(status);
  const tone = (mode: PaletteMode) => semantic[mode][meta.tone];

  return (
    <Chip
      size="small"
      label={label ?? meta.label}
      sx={{
        // The semantic tokens are mode-aware: `light` is the tint, `dark` the
        // strong text on it, in either colour mode.
        backgroundColor: (t) => tone(t.palette.mode).light,
        color: (t) => tone(t.palette.mode).dark,
        // Neutral's tint sits close to the surface, so it alone takes a
        // visible border; the rest keep a transparent one to match its size.
        border: (t) => `1px solid ${meta.tone === 'neutral' ? t.palette.divider : 'transparent'}`,
        fontWeight: 600,
        // Leading status dot in the text colour.
        '&::before': {
          content: '""',
          flexShrink: 0,
          width: 6,
          height: 6,
          ml: 1,
          borderRadius: '50%',
          backgroundColor: 'currentColor',
        },
        '& .MuiChip-label': { pl: 0.75 },
        ...sx,
      }}
      {...chipProps}
    />
  );
};

export default StatusChip;
